/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import {
    IMultiLevelDatasetNode,
    IMultiLevelDatasetVisitor
} from '../../../../store/types/MultiLevelDatasetTypes';
import { IEmptyDetailsConverter, IVisitNodeMapper, IVisitorContext } from '../types/tableVisualizationConverterTypes';
import defaultPreVisitNodeMapper from './preVisitNodeMapper';
import defaultPostVisitNodeMapper from './postVisitNodeMapper';
import defaultToEmptyDetailsConverter from './toEmptyRowsConverter';
import { ITableVisualizationWrapperProps, RowType } from '../types/tableVisualizationTypes';

function calculateStartRowRenderIndex(offsetTop: number, rowHeights: number[]): number {
    let index = 0;
    let calculatedHeight = 0;

    while (calculatedHeight < offsetTop) {
        calculatedHeight += rowHeights[index];
        index += 1;
    }

    return index;
}

export function createVisitorFactory(preVisitNodeMapper: IVisitNodeMapper, postVisitNodeMapper: IVisitNodeMapper, toEmptyRowsConverter: IEmptyDetailsConverter) {
    return function createVisitor(props: ITableVisualizationWrapperProps): IMultiLevelDatasetVisitor<IVisitorContext> {

        const {
            dataset,
            staticProperties,
            dynamicProperties,
        } = props;

        const startRowRenderIndex = calculateStartRowRenderIndex(dynamicProperties.offsetTop, dynamicProperties.rowHeights);

        return {
            preVisitNode(node: IMultiLevelDatasetNode, context: IVisitorContext) {
                if (context.addedRowsCount < dynamicProperties.rowsCountToRender) {
                    const ctxt = context;

                    if (node.values.length) {
                        ctxt.lastDetailIndex += 1;
                    }

                    const convertByNodeType = preVisitNodeMapper[node.nodeType];
                    if (convertByNodeType) {
                        return convertByNodeType(node, context, staticProperties, dynamicProperties, startRowRenderIndex);
                    }
                }
                return context;
            },

            postVisitNode(node: IMultiLevelDatasetNode, context: IVisitorContext) {
                if (context.addedRowsCount < dynamicProperties.rowsCountToRender) {
                    const convertByNodeType = postVisitNodeMapper[node.nodeType];
                    if (convertByNodeType) {
                        return convertByNodeType(node, context, staticProperties, dynamicProperties, startRowRenderIndex);
                    }
                }
                return context;
            },

            postVisit(context: IVisitorContext) {
                let newContext = context;
                // if the dataset does not contain enough rows and it is not complete yet
                // fill missing rows with filler elements
                if (context.addedRowsCount < dynamicProperties.rowsCountToRender && !dataset.summary.complete) {
                    newContext = toEmptyRowsConverter[RowType.DETAIL](context, staticProperties, dynamicProperties);
                    newContext.missingIndex = newContext.lastDetailIndex + 1;
                }
                return newContext;
            }
        };
    }
}

export const visitorFactory = {
    create: createVisitorFactory(defaultPreVisitNodeMapper, defaultPostVisitNodeMapper, defaultToEmptyDetailsConverter)
};
